import { textColorFields } from '../_shared/textColor.js';
import { FEATURED_GALLERY_SETTINGS } from './gallery.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { mediaGalleryBlockSchema } from '../../blocks/media-gallery/index.js';
import { titleBlockSchema } from '../../blocks/title/index.js';
import { ratingBlockSchema } from '../../blocks/rating/index.js';
import { priceBlockSchema } from '../../blocks/price/index.js';
import { variantPickerBlockSchema } from '../../blocks/variant-picker/index.js';
import { quantityBlockSchema } from '../../blocks/quantity/index.js';
import { buyButtonsBlockSchema } from '../../blocks/buy-buttons/index.js';
import { descriptionBlockSchema } from '../../blocks/description/index.js';
import { viewDetailsBlockSchema } from '../../blocks/view-details/index.js';
import { collapsibleRowBlockSchema } from '../../blocks/collapsible-row/index.js';
import { imageBlockSchema } from '../../blocks/image/index.js';
import { skuBlockSchema } from '../../blocks/sku/index.js';
import { shareButtonBlockSchema } from '../../blocks/share-button/index.js';
/**
 * featured-product — Shopify's "Featured product" section: ONE picked product shown as a buy-box, built
 * exactly like Shopify — a section with a FLAT, editable list of CHILD BLOCKS (Title · Price · Variant
 * picker · Quantity selector · Media gallery · Buy buttons · View full details, + "Add block"). Each block
 * is a reusable registry block that renders + is configured independently, and the seller can
 * add/remove/reorder/hide them from the builder rail.
 *
 * It differs from `main-product` two ways on purpose: (1) it shows a PICKED product (its `product` picker)
 * rather than the route product, so it drops on home/collection/page; (2) its blocks are a FLAT list (no
 * `gallery`/`details` static container columns) — the Component splits the media block into the media
 * column and the rest into the (sticky) info column. Until a product is picked (or in the builder preview)
 * it falls back to the demo product, so a fresh section renders a complete buy experience.
 */
/** The block types this section hosts. The media-gallery renders in the media column; the rest stack in the
 *  info column. Curated to the Shopify featured-product buy-box set (+ a few extra content blocks). */
const FP_ACCEPTS = [
    'media-gallery',
    'image',
    'title',
    'rating',
    'price',
    'variant-picker',
    'quantity',
    'buy-buttons',
    'description',
    'view-details',
    'collapsible-row',
    'sku',
    'share-button',
];
export const featuredProductSchema = {
    type: 'featured-product',
    name: 'Featured product',
    // v4: the media slot is the product `media-gallery` (all the picked product's images, two-column —
    // like the product page). v3 was block-based with a standalone `image` media slot (flat child list);
    // v2 was a fixed no-children section; v1 aliased main-product.
    version: 4,
    // A general merchandising section — insertable on home / collection / page (NOT tied to the product route
    // the way main-product is).
    templates: ['index', 'collection', 'page', 'thank-you'],
    fields: [
        {
            id: 'product',
            type: 'product-picker',
            label: 'Product',
            group: 'Product',
            info: 'Choose the product to feature. A sample product is shown until you pick one.',
        },
        {
            id: 'mediaSize',
            type: 'select',
            label: 'Media size',
            group: 'Product layout',
            default: 'medium',
            options: [
                { value: 'small', label: 'Small (42%)' },
                { value: 'medium', label: 'Medium (48%)' },
                { value: 'large', label: 'Large (56%)' },
            ],
        },
        {
            id: 'mediaPosition',
            type: 'select',
            label: 'Media position',
            group: 'Product layout',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'enableStickyInfo',
            type: 'checkbox',
            label: 'Sticky info on desktop',
            group: 'Product layout',
            info: 'The product-info column stays in view as the gallery scrolls (desktop only).',
            default: true,
        },
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    // Registry-model children: a flat, editable block list (like Shopify's Featured product). No static
    // gallery/details columns — the Component splits the media block from the info blocks at render.
    accepts: [...FP_ACCEPTS],
    blocks: [
        mediaGalleryBlockSchema,
        imageBlockSchema,
        titleBlockSchema,
        ratingBlockSchema,
        priceBlockSchema,
        variantPickerBlockSchema,
        quantityBlockSchema,
        buyButtonsBlockSchema,
        descriptionBlockSchema,
        viewDetailsBlockSchema,
        collapsibleRowBlockSchema,
        skuBlockSchema,
        shareButtonBlockSchema,
    ],
    maxBlocks: 20,
};
/**
 * Default = a clean featured-product buy-box as a FLAT block list: Media gallery · Title · Price ·
 * Variant picker · Quantity · Buy buttons · View full details — i.e. the PRODUCT PAGE buy-box with a
 * picked product. The media slot is the `media-gallery` block (same settings as the `main-product`
 * preset), so picking a product shows ALL of its images — main image + thumbnail strip + lightbox —
 * exactly like the product page, and the section lays out in TWO COLUMNS (gallery | sticky info).
 * With no product picked the gallery falls back to the neutral scenic placeholder (the demo product
 * ships no images), never stock apparel photos.
 */
export const featuredProductPresets = [
    {
        name: 'Featured product',
        settings: {
            mediaSize: 'medium',
            mediaPosition: 'left',
            enableStickyInfo: true,
            colorScheme: 'light',
            paddingTop: 48,
            paddingBottom: 48,
            width: 'wide',
        },
        blocks: [
            // The product gallery, configured EXACTLY like the `main-product` preset (thumbnails below the
            // main image, no overlay arrows, `contain` fit) so a featured product reads like the product page.
            { type: 'media-gallery', settings: FEATURED_GALLERY_SETTINGS },
            { type: 'title' },
            { type: 'price' },
            { type: 'variant-picker', settings: { variantStyle: 'auto' } },
            { type: 'quantity' },
            {
                type: 'buy-buttons',
                settings: {
                    atcCustomLabel: '@t:product.add_to_cart',
                    uppercaseText: true,
                    enableSecondaryBtn: true,
                    secondaryBtnLabel: '@t:product.buy_now',
                },
            },
            { type: 'view-details' },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const featuredProductMeta = {
    label: 'Featured product',
    group: 'Product',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map